// Pure month-grid date math for the Calendar view. No Firebase.

import type { Initiative, ISODateString } from '../types';

export interface MonthGrid {
  /** Year/month actually being shown, e.g. "September 2026". */
  label: string;
  year: number;
  /** 0-11 */
  month: number;
  /** Always a multiple of 7 — full weeks, including the lead/trail days from
   *  the adjacent months (like every real calendar). */
  days: Array<{ iso: ISODateString; day: number; inMonth: boolean; isToday: boolean }>;
}

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function toISO(y: number, m: number, d: number): ISODateString {
  return `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

/** Build the grid for the month containing `anchor` (year, 0-based month). */
export function buildMonthGrid(year: number, month: number, todayIso: ISODateString): MonthGrid {
  const first = new Date(Date.UTC(year, month, 1));
  const startOffset = first.getUTCDay(); // 0 = Sunday
  const gridStart = new Date(Date.UTC(year, month, 1 - startOffset));

  const lastOfMonth = new Date(Date.UTC(year, month + 1, 0));
  const totalDaysNeeded = startOffset + lastOfMonth.getUTCDate();
  const weeks = Math.ceil(totalDaysNeeded / 7);
  const cellCount = weeks * 7;

  const days: MonthGrid['days'] = [];
  for (let i = 0; i < cellCount; i++) {
    const d = new Date(gridStart.getTime() + i * 86_400_000);
    const y = d.getUTCFullYear();
    const m = d.getUTCMonth();
    const day = d.getUTCDate();
    const iso = toISO(y, m, day);
    days.push({ iso, day, inMonth: m === month && y === year, isToday: iso === todayIso });
  }

  return {
    label: first.toLocaleDateString(undefined, { month: 'long', year: 'numeric', timeZone: 'UTC' }),
    year,
    month,
    days,
  };
}

export function weekdayLabels(): string[] {
  return WEEKDAY_LABELS;
}

/** Every ISO date between `start` and `end` inclusive (or just `start` if
 *  `end` is missing/not after it). */
export function datesInRange(
  start: ISODateString,
  end?: ISODateString | null,
): ISODateString[] {
  if (!end || end <= start) return [start];
  const [y1, m1, d1] = start.split('-').map(Number);
  const [y2, m2, d2] = end.split('-').map(Number);
  let t = Date.UTC(y1, m1 - 1, d1);
  const endMs = Date.UTC(y2, m2 - 1, d2);
  const out: ISODateString[] = [];
  while (t <= endMs) {
    const d = new Date(t);
    out.push(toISO(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
    t += 86_400_000;
  }
  return out;
}

/** Every ISO date an initiative occupies on the calendar: eventDate through
 *  eventEndDate inclusive, plus any extra occurrence dates (monthly). */
export function datesForInitiative(init: {
  eventDate?: ISODateString | null;
  eventEndDate?: ISODateString | null;
  occurrenceDates?: ISODateString[];
}): ISODateString[] {
  if (!init.eventDate) return [];
  const out = datesInRange(init.eventDate, init.eventEndDate);
  for (const extra of init.occurrenceDates ?? []) {
    if (!out.includes(extra)) out.push(extra);
  }
  return out;
}

/** Presentation-only district filter over an ALREADY AUTHORIZED Initiative
 * set. The caller must obtain that set through useVisibleInitiatives(); this
 * helper never discovers or widens access to additional records. */
export function initiativesForCalendar(
  initiatives: Initiative[],
  districtFilter: string,
): Initiative[] {
  return districtFilter === 'All'
    ? initiatives
    : initiatives.filter((initiative) => initiative.districtId === districtFilter);
}

/** Project Initiative dates into Calendar days without creating another
 * event record or data model. Undated Initiatives naturally contribute no
 * entries because datesForInitiative() returns an empty list for them. */
export function initiativesByCalendarDate(initiatives: Initiative[]): Map<ISODateString, Initiative[]> {
  const byDate = new Map<ISODateString, Initiative[]>();
  for (const initiative of initiatives) {
    for (const date of datesForInitiative(initiative)) {
      const entries = byDate.get(date) ?? [];
      entries.push(initiative);
      byDate.set(date, entries);
    }
  }
  return byDate;
}
